// src/redux/companiesSlice.js

import api from './config';
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { logout } from './authSlice';

// ─── Fetch Companies (liste datatable) ────────────────────────────────────
export const fetchCompanies = createAsyncThunk(
  'companies/fetchCompanies',
  async (params, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.get('/company/allCompanies', { params });
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

// ─── Fetch Company With Subscriptions ─────────────────────────────────────
// Route : GET /company/:id/subscriptions
// Retourne la company avec subscriptions._id en strings ET planId populé
// C'est CE thunk qu'on utilise dans PaymentHistoryTable (pas fetchCompanies)
export const fetchCompanySubscriptions = createAsyncThunk(
  'companies/fetchCompanySubscriptions',
  async (companyId, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.get(`/company/${companyId}/subscriptions`);
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

// ─── Fetch Abonnements ────────────────────────────────────────────────────
// Route : GET /abonnement/all  → retourne Abonnement[] directement
export const fetchAbonnements = createAsyncThunk(
  'companies/fetchAbonnements',
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.get('/abonnement/all');
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

// ─── Company Status ────────────────────────────────────────────────────────
export const updatedCompanyStatus = createAsyncThunk(
  'company/updateStatus',
  async (params, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.put('/company/status', params);
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

// ─── GET Payment Proof ─────────────────────────────────────────────────────
// Route : GET /company/:companyId/subscription/:subscriptionId/payment-proof
export const getPaymentProof = createAsyncThunk(
  'company/getPaymentProof',
  async ({ companyId, subscriptionId }, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.get(
        `/company/${companyId}/subscription/${subscriptionId}/payment-proof`,
        { responseType: 'arraybuffer' }
      );
      const contentType = response.headers['content-type'] || 'application/octet-stream';
      const blob        = new Blob([response.data], { type: contentType });
      const blobUrl     = URL.createObjectURL(blob);
      return { blobUrl, contentType };
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (error.response?.data instanceof ArrayBuffer) {
        try {
          const text = new TextDecoder().decode(error.response.data);
          const json = JSON.parse(text);
          return rejectWithValue(json?.message || `HTTP ${error.response.status}`);
        } catch {
          return rejectWithValue(`HTTP ${error.response?.status || 'unknown'}`);
        }
      }
      return rejectWithValue(error.response?.data?.message || error.message || 'Erreur chargement');
    }
  }
);

// ─── Upload Payment Proof ──────────────────────────────────────────────────
// Route : POST /company/payment-proof/:subscriptionId
// Field : 'file' (FileInterceptor('file'))
export const uploadPaymentProof = createAsyncThunk(
  'company/uploadPaymentProof',
  async ({ subscriptionId, file }, { rejectWithValue, dispatch }) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.post(
        `/company/payment-proof/${subscriptionId}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      dispatch(fetchCompanies());
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

// ─── Validate Payment Proof ────────────────────────────────────────────────
// Route : PUT /company/subscription/:subscriptionId/validate
// Guard : JwtAuthGuard uniquement (RoleGuard supprimé côté backend)
// Body  : { action: 'APPROVE' | 'REJECT', reason?: string }
export const validatePaymentProof = createAsyncThunk(
  'company/validatePaymentProof',
  async ({ subscriptionId, action, reason }, { rejectWithValue, dispatch }) => {
    try {
      const body = { action };
      if (action === 'REJECT' && reason) body.reason = reason;
      const response = await api.put(
        `/company/subscription/${subscriptionId}/validate`,
        body
      );
      dispatch(fetchCompanies());
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// ─── Extend/Relancer un Trial ──────────────────────────────────────────────
// Route : PUT /company/subscription/:subscriptionId/extend-trial
// Guard : JwtAuthGuard + RoleGuard + PermissionGuard('entreprises.prolongerEssai')
//         (SUPER_ADMIN toujours autorisé, SUPER_ADMIN_ASSISTANT si la permission est cochée)
// Body  : { startDate: ISOString, endDate: ISOString }
export const extendTrialSubscription = createAsyncThunk(
  'company/extendTrialSubscription',
  async ({ subscriptionId, startDate, endDate }, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.put(
        `/company/subscription/${subscriptionId}/extend-trial`,
        { startDate, endDate }
      );
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// ─── Update Billing Country ────────────────────────────────────────────────
// Route : PUT /company/:companyId/billing-country
// Guard : SUPER_ADMIN uniquement (pas les assistants)
// Body  : { billingCountry: 'SA' } — décide de la devise et du moyen de paiement
//         des prochaines commandes (SA → SAR par carte, TN → TND, autre → USD, par virement)
export const updateBillingCountry = createAsyncThunk(
  'company/updateBillingCountry',
  async ({ companyId, billingCountry }, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.put(`/company/${companyId}/billing-country`, { billingCountry });
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// ─── Delete Company ───────────────────────────────────────────────────────
export const deleteCompany = createAsyncThunk(
  'companies/deleteCompany',
  async (id, { rejectWithValue, dispatch }) => {
    try {
      await api.delete(`/company/${id}`);
      return id;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

// ─── Subscribe ─────────────────────────────────────────────────────────────
// Route : POST /company/subscribe
export const createSubscription = createAsyncThunk(
  'company/createSubscription',
  async ({ planId, duration, paymentMethod, amount, currency, paymentProofFile }, { rejectWithValue, dispatch }) => {
    try {
      const formData = new FormData();
      formData.append('planId', planId);
      formData.append('duration', duration);
      formData.append('paymentMethod', paymentMethod);
      formData.append('amount', String(amount));
      if (currency) formData.append('currency', currency);
      if (paymentProofFile) formData.append('file', paymentProofFile);
      const response = await api.post('/company/subscribe', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      dispatch(fetchCompanies());
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

// ─── Slice ─────────────────────────────────────────────────────────────────
const companiesSlice = createSlice({
  name: 'companies',
  initialState: {
    companies: [],
    loading: false,
    isSuccess: false,
    errors: [],
    validationLoading: false,
    proofLoading: false,
    abonnements: [],
    abonnementsLoading: false,
    // company avec subscriptions sérialisées correctement
    currentCompanySubscriptions: null,
    subscriptionsLoading: false,
    extendTrialLoading: false,
  },
  reducers: {
    clearErrors: (state) => { state.errors = []; },
    updateCompanyLocal: (state, { payload }) => {
      const { companyId, updates } = payload;
      const i = state.companies.findIndex(c => c._id === companyId || c.DT_RowId === companyId);
      if (i !== -1) state.companies[i] = { ...state.companies[i], ...updates };
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchCompanies
      .addCase(fetchCompanies.pending,   s => { s.loading = true; })
      .addCase(fetchCompanies.fulfilled, (s, { payload }) => {
        s.isSuccess = true; s.companies = payload?.data || []; s.recordsTotal = payload?.recordsTotal || 0; s.loading = false;
      })
      .addCase(fetchCompanies.rejected, (s, { payload }) => {
        if (payload?.statusCode === 400) s.errors = payload?.message;
        s.isSuccess = false; s.loading = false;
      })

      // fetchCompanySubscriptions
      .addCase(fetchCompanySubscriptions.pending,   s => { s.subscriptionsLoading = true; })
      .addCase(fetchCompanySubscriptions.fulfilled, (s, { payload }) => {
        s.currentCompanySubscriptions = payload;
        s.subscriptionsLoading = false;
      })
      .addCase(fetchCompanySubscriptions.rejected,  s => { s.subscriptionsLoading = false; })

      // fetchAbonnements
      .addCase(fetchAbonnements.pending,   s => { s.abonnementsLoading = true; })
      .addCase(fetchAbonnements.fulfilled, (s, { payload }) => {
        s.abonnements = Array.isArray(payload) ? payload : (payload?.data || []);
        s.abonnementsLoading = false;
      })
      .addCase(fetchAbonnements.rejected,  s => { s.abonnementsLoading = false; })

      // updatedCompanyStatus
      .addCase(updatedCompanyStatus.pending,   s => { s.loading = true; })
      .addCase(updatedCompanyStatus.fulfilled, (s, { payload }) => {
        s.isSuccess = true;
        s.companies = s.companies.map(c =>
          (c.DT_RowId === payload?._id || c._id === payload?._id)
            ? { ...payload, DT_RowId: payload._id, admin: c.admin } : c
        );
        s.loading = false;
      })
      .addCase(updatedCompanyStatus.rejected, (s, { payload }) => {
        if (payload?.statusCode === 400) s.errors = payload?.message;
        s.isSuccess = false; s.loading = false;
      })

      // getPaymentProof (blobUrl non stocké dans Redux)
      .addCase(getPaymentProof.pending,   s => { s.proofLoading = true; })
      .addCase(getPaymentProof.fulfilled, s => { s.proofLoading = false; })
      .addCase(getPaymentProof.rejected,  s => { s.proofLoading = false; })

      // validatePaymentProof
      .addCase(validatePaymentProof.pending,   s => { s.validationLoading = true; })
      .addCase(validatePaymentProof.fulfilled, s => { s.isSuccess = true; s.validationLoading = false; })
      .addCase(validatePaymentProof.rejected,  (s, { payload }) => {
        if (payload?.statusCode === 400) s.errors = payload?.message;
        s.isSuccess = false; s.validationLoading = false;
      })

      // extendTrialSubscription
      .addCase(extendTrialSubscription.pending,   s => { s.extendTrialLoading = true; })
      .addCase(extendTrialSubscription.fulfilled, s => { s.isSuccess = true; s.extendTrialLoading = false; })
      .addCase(extendTrialSubscription.rejected,  (s, { payload }) => {
        if (payload?.statusCode === 400) s.errors = payload?.message;
        s.isSuccess = false; s.extendTrialLoading = false;
      })

      // createSubscription
      .addCase(createSubscription.pending,   s => { s.loading = true; })
      .addCase(createSubscription.fulfilled, s => { s.isSuccess = true; s.loading = false; })
      .addCase(createSubscription.rejected,  (s, { payload }) => {
        if (payload?.statusCode === 400) s.errors = payload?.message;
        s.isSuccess = false; s.loading = false;
      })

      // uploadPaymentProof
      .addCase(uploadPaymentProof.pending,   s => { s.loading = true; })
      .addCase(uploadPaymentProof.fulfilled, s => { s.isSuccess = true; s.loading = false; })
      .addCase(uploadPaymentProof.rejected,  (s, { payload }) => {
        if (payload?.statusCode === 400) s.errors = payload?.message;
        s.isSuccess = false; s.loading = false;
      })

      // deleteCompany
      .addCase(deleteCompany.pending,   s => { s.loading = true; })
      .addCase(deleteCompany.fulfilled, (s, { payload }) => {
        s.companies = s.companies.filter(c => c._id !== payload && c.DT_RowId !== payload);
        s.isSuccess = true; s.loading = false;
      })
      .addCase(deleteCompany.rejected,  (s, { payload }) => {
        if (payload?.statusCode === 400) s.errors = payload?.message;
        s.isSuccess = false; s.loading = false;
      });
  },
});

export const { clearErrors, updateCompanyLocal } = companiesSlice.actions;
export default companiesSlice.reducer;
